import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useNavigate } from 'react-router-dom';
import useCustomerPlan from '../../utils/hooks/useCustomerPlan';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../redux/selectors/dataModeler';
import {
  dataModelAddDraftFactTableData,
  dataModelerAddDraftDimensionData,
  dataModelerDiscardCurrentEdits,
  dataModelerSetCurrentSchemaId,
  dataModelerSetLockedTtlSeconds,
  dataModelerSetSchemaDataModel,
  dataModelerSetUpdateInProgress,
  dataModelerToggleExpandTable,
  IDataModelerDraftEdits,
} from '../../redux/slices/dataModeler';
import usePermission from '../../utils/hooks/permissionHooks';
import {
  IBootstrapQueryQuery,
  IBootstrapQueryQueryVariables,
  IDataModelAddFieldInput,
  IDataModelCreateMutation,
  IDataModelCreateMutationVariables,
  IDataModelEditJoinInput,
  IDataModelExtendFactFieldInput,
  IDataModelUpdateMutation,
  IDataModelUpdateMutationVariables,
  ISchemaDataModelFieldsFragment,
  PermissionKey,
  UserSettingKind,
} from '../../utils/apollo/graphqlSchema';
import { useDataModelerEditFeature } from '../../utils/hooks';
import { defaultEmptyDraftEdits } from '../../redux/slices/dataModeler/dataModelerSlice';
import {
  schemaDraftIsDirty,
  useDataModelerEditorCanConfirm,
} from './DataModelerSchema/validation';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DATA_MODEL_CREATE,
  DATA_MODEL_UPDATE,
} from '../../utils/apollo/dataModeler/mutation';
import { bootstrapQuery } from '../../utils/apollo/globalConfig';
import { batch } from 'react-redux';
import { updateSchemaConfig } from '../../redux/slices/bootstrap';
import { setDefaultSchemaId } from '../../redux/slices/userSettings';
import { updateDefaultSchemaSetting } from '../../redux/slices/permission';
import { assertNotNil } from '../../utils/types';
import {
  dimensionDraftDataToAddedDimensionInput,
  editTableDraftDataToEditTableInput,
  factDraftDataToAddedFactInput,
  getAllModelFields,
  joinDataToJoinInput,
} from './DataModelerSchema/utils';
import toastMessage from '../../utils/toastMessage';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { appRoutes } from '../../path';
import { MaybeNil } from '../../utils/interfaces';

export interface IAddTable {
  viewKey: string;
  tableKey: string;
  sqlTableName: string;
  isDimension: boolean;
}

interface IDataModelerContext {
  schemaObj: MaybeNil<ISchemaDataModelFieldsFragment>;
  draftEdits: IDataModelerDraftEdits;
  isEditMode: boolean;
  handleAddTable: (values: IAddTable) => void;
  updateInProgress: boolean;
  schemaIsDirty: boolean;
  canConfirm: boolean;
  lockedTtlSeconds: number;
  handleSubmitChanges: () => Promise<void>;
  handleLockedTtlExpired: () => void;
  progressText: string;
  addedViewKeys: string[];
  addedTableKeys: string[];
  numTables: number;
  numFields: number;
}

const DataModelerContext = createContext<IDataModelerContext | null>(null);

export const useDataModelerContext = () => {
  const context = useContext(DataModelerContext);

  if (context === null) {
    throw new Error(
      'useDataModelerContext must be used within a DataModelerProvider',
    );
  }

  return context;
};

export const DataModelerProvider = ({ children }: PropsWithChildren<any>) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { refetchCustomerPlan } = useCustomerPlan();

  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);

  // this is only used to display proper toast message if this window confirmed it.
  const [submittedSchemaToast, setSubmittedSchemaToast] = useState<
    string | null
  >(null);

  const lockedTtlSeconds = useAppSelector(
    (rootState) => rootState.dataModeler.lockedTtlSeconds,
  );
  const setLockedTtlSeconds = useCallback(
    (n: number) => {
      dispatch(dataModelerSetLockedTtlSeconds(n));
    },
    [dispatch],
  );

  const { checkIsPermitted } = usePermission();

  const isPermittedToEditDataModeler = schemaObj
    ? checkIsPermitted({
        requiredPermissionKeys: [
          PermissionKey.DataModelView,
          PermissionKey.DataModelEdit,
        ],
        schemaId: schemaObj.key,
      })
    : false;

  const dataModelerEditFeatureEnabled = useDataModelerEditFeature();
  const isEditMode =
    isPermittedToEditDataModeler && dataModelerEditFeatureEnabled;
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  const schemaIsDirty = schemaDraftIsDirty(draftEdits);
  const updateInProgress = useAppSelector(
    (rootState) => rootState.dataModeler.updateInProgress,
  );

  const canConfirm = useDataModelerEditorCanConfirm();

  const [dataModelUpdate] = useMutation<
    IDataModelUpdateMutation,
    IDataModelUpdateMutationVariables
  >(DATA_MODEL_UPDATE);

  const [dataModelCreate] = useMutation<
    IDataModelCreateMutation,
    IDataModelCreateMutationVariables
  >(DATA_MODEL_CREATE);

  const [bootstrap] = useLazyQuery<
    IBootstrapQueryQuery,
    IBootstrapQueryQueryVariables
  >(bootstrapQuery, {
    variables: {
      dashboardSidebarKind: UserSettingKind.DashboardSidebar,
      executionHistoryExpandedKind: UserSettingKind.ExecutionHistoryExpanded,
      jupiterHostKind: UserSettingKind.JupyterHost,
      quickAccessDictionaryKind: UserSettingKind.QuickAccessDictionary,
      defaultViewKind: UserSettingKind.DefaultView,
      defaultSchemaKind: UserSettingKind.DefaultSchema,
      sidebarPinnedKind: UserSettingKind.SidebarPinned,
      sidebarSizeKind: UserSettingKind.SidebarSize,
      colorSchemaKind: UserSettingKind.ColorSchema,
    },
  });

  const handleDiscardEdits = useCallback(
    () => dispatch(dataModelerDiscardCurrentEdits()),
    [dispatch],
  );

  const handleAddTable = useCallback(
    ({ viewKey, tableKey, sqlTableName, isDimension }: IAddTable) => {
      if (isDimension) {
        dispatch(
          dataModelerAddDraftDimensionData({
            viewKey,
            tableKey,
            sqlTableName,
          }),
        );
      } else {
        dispatch(
          dataModelAddDraftFactTableData({
            viewKey,
            tableKey,
            sqlTableName,
          }),
        );
      }
      dispatch(dataModelerToggleExpandTable({ tableKey, expand: true }));
    },
    [dispatch],
  );

  // A helper to update the local bootstrap state after a successful schema update
  const updateBootstrapSchemaConfig = useCallback(async () => {
    const bootstrapResult = await bootstrap();
    if (bootstrapResult.data?.schemaConfig) {
      const { schemaConfig, defaultSchemaSetting } = bootstrapResult.data;
      batch(() => {
        dispatch(updateSchemaConfig(schemaConfig));
        if (schemaConfig?.defaultSchemaId) {
          dispatch(setDefaultSchemaId(schemaConfig?.defaultSchemaId));
        }
        if (defaultSchemaSetting) {
          dispatch(updateDefaultSchemaSetting({ defaultSchemaSetting }));
        }
      });
    }
  }, [bootstrap, dispatch]);

  const handleSubmitChanges = useCallback(async () => {
    assertNotNil(schemaObj);
    dispatch(dataModelerSetUpdateInProgress(true));
    const successToastMessage = `Schema ${
      draftEdits.updatedName ?? schemaObj.name
    } is ${draftEdits.isNew ? 'created' : 'updated'}.`;
    setSubmittedSchemaToast(successToastMessage);
    const isNewSchema = draftEdits.isNew;
    const addedFieldsInputs: IDataModelAddFieldInput[] = [];
    const addedExtendFieldInputs: IDataModelExtendFactFieldInput[] = [];
    draftEdits.draftFieldsData.forEach((draftField) => {
      if (draftField.addInput) {
        addedFieldsInputs.push(draftField.addInput);
      }
      if (draftField.extendInput) {
        addedExtendFieldInputs.push(draftField.extendInput);
      }
    });
    const editTableInputs = draftEdits.editTablesData.map((editTableData) => {
      return editTableDraftDataToEditTableInput(editTableData);
    });
    const editedJoinInputs: IDataModelEditJoinInput[] =
      draftEdits.editJoinsData.map((editJoinData) => {
        const { viewKey, joinData } = editJoinData;
        return {
          viewKey,
          join: joinDataToJoinInput(
            viewKey,
            joinData,
            schemaObj,
            addedFieldsInputs,
          ),
        };
      });
    const addedDimensionInputs = draftEdits.addedDimensionsData.map((dimData) =>
      dimensionDraftDataToAddedDimensionInput(
        dimData,
        schemaObj,
        addedFieldsInputs,
      ),
    );
    const addedFactTableInputs = draftEdits.addedFactsData.map(
      factDraftDataToAddedFactInput,
    );
    const abortOnError = () => {
      dispatch(dataModelerSetUpdateInProgress(false));
      setLockedTtlSeconds(0);
      setSubmittedSchemaToast(null);
    };
    const catchException = async () => {
      toastMessage({
        message: 'Unexpected error, please try again.',
        type: 'error',
      });
      abortOnError();
    };
    const resp = isNewSchema
      ? await dataModelCreate({
          variables: {
            input: {
              schema: schemaObj.key,
              name: schemaObj.name,
              factViewKey: schemaObj.fact.key,
              dataSource: draftEdits.dataSource!!,
              endDate: draftEdits.endDate,
              addFactTables: addedFactTableInputs,
              addDimensions: addedDimensionInputs,
              addFields: addedFieldsInputs,
              extendFactFields: addedExtendFieldInputs,
              addDerivedFields: draftEdits.draftDerivedFields,
              addSubjects: draftEdits.addedSubjects.map((subj) => subj.input),
              sampling: draftEdits.sampling,
              addFunnelPartitions: draftEdits.addFunnelPartitions,
              dataRefresh: draftEdits.dataRefresh,
              disabled: draftEdits.disabled ?? false,
              description: draftEdits.description,
            },
          },
        })
          .then(async (res) => {
            return res.data?.dataModelCreate;
          })
          .catch(catchException)
      : await dataModelUpdate({
          variables: {
            input: {
              schema: schemaObj.key,
              basedOnVersion: schemaObj.version,
              editTables: editTableInputs,
              editJoins: editedJoinInputs,
              addFactTables: addedFactTableInputs,
              addDimensions: addedDimensionInputs,
              addFields: addedFieldsInputs,
              extendFactFields: addedExtendFieldInputs,
              editFields: draftEdits.updatedFieldsInputs,
              editColumns: draftEdits.editColumnInputs,
              addDerivedFields: draftEdits.draftDerivedFields,
              addSubjects: draftEdits.addedSubjects.map((subj) => subj.input),
              updatedName: draftEdits.updatedName,
              sampling: draftEdits.sampling,
              dataSource: draftEdits.dataSource,
              dataRefresh: draftEdits.dataRefresh,
              endDate: draftEdits.endDate,
              addFunnelPartitions: draftEdits.addFunnelPartitions,
              disabled: draftEdits.disabled,
              description: draftEdits.description,
            },
          },
        })
          .then(async (res) => {
            return res.data?.dataModelUpdate;
          })
          .catch(catchException);
    if (printGraphqlErrors(resp?.errors)) {
      abortOnError();
      return;
    }
    await updateBootstrapSchemaConfig();
    await refetchCustomerPlan();
    assertNotNil(resp?.entity);
    const { entity } = resp;
    setLockedTtlSeconds(entity.lockedTtlSeconds ?? 5);
    batch(() => {
      dispatch(
        dataModelerSetSchemaDataModel({ newModel: entity, setAsCurrent: true }),
      );
      dispatch(dataModelerSetUpdateInProgress(false));
      dispatch(dataModelerDiscardCurrentEdits());
    });
  }, [
    schemaObj,
    dispatch,
    draftEdits,
    dataModelCreate,
    dataModelUpdate,
    setLockedTtlSeconds,
    updateBootstrapSchemaConfig,
    refetchCustomerPlan,
  ]);

  const handleLockedTtlExpired = useCallback(() => {
    setLockedTtlSeconds(0);
    if (schemaObj) {
      batch(() => {
        dispatch(dataModelerDiscardCurrentEdits());
        dispatch(
          dataModelerSetCurrentSchemaId({
            schemaId: schemaObj.key,
            reset: true,
          }),
        );
        navigate(appRoutes.modeler.modeler(schemaObj.key));
      });
    }
    const message = submittedSchemaToast
      ? submittedSchemaToast
      : 'Data model is updated.';
    toastMessage({
      message,
      type: 'info',
    });
    setSubmittedSchemaToast(null);
  }, [
    dispatch,
    navigate,
    schemaObj,
    setLockedTtlSeconds,
    submittedSchemaToast,
  ]);

  const progressText = updateInProgress ? 'Validating' : 'Updating';

  const addedViewKeys = useMemo(
    () => draftEdits.addedDimensionsData.map((dim) => dim.viewKey),
    [draftEdits.addedDimensionsData],
  );

  const addedTableKeys = useMemo(
    () => [
      ...draftEdits.addedFactsData.map((factData) => factData.tableKey),
      ...draftEdits.addedDimensionsData.map((dimData) => dimData.tableKey),
    ],
    [draftEdits.addedDimensionsData, draftEdits.addedFactsData],
  );

  const numTables = (schemaObj?.tables.length ?? 0) + addedTableKeys.length;
  const numFields =
    getAllModelFields(schemaObj).length +
    draftEdits.draftFieldsData.filter((draft) => !!draft.addInput).length +
    draftEdits.draftDerivedFields.length;

  const contextValues = useMemo<IDataModelerContext>(
    () => ({
      isEditMode,
      handleDiscardEdits,
      handleAddTable,
      updateInProgress,
      schemaIsDirty,
      canConfirm,
      lockedTtlSeconds,
      handleSubmitChanges,
      handleLockedTtlExpired,
      progressText,
      addedViewKeys,
      addedTableKeys,
      numTables,
      numFields,
      draftEdits,
      schemaObj,
    }),
    [
      addedTableKeys,
      addedViewKeys,
      canConfirm,
      draftEdits,
      handleAddTable,
      handleDiscardEdits,
      handleLockedTtlExpired,
      handleSubmitChanges,
      isEditMode,
      lockedTtlSeconds,
      numFields,
      numTables,
      progressText,
      schemaIsDirty,
      schemaObj,
      updateInProgress,
    ],
  );
  return (
    <DataModelerContext.Provider value={contextValues}>
      {children}
    </DataModelerContext.Provider>
  );
};
